import { Breadcrumb } from '@/components/ui/Breadcrumb';
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { BlogPost } from '@/types';
import { Button } from '@/components/ui/Button';
import { SocialShare } from '@/components/ui/SocialShare';
import { calculateReadingTime, cleanBlogDescription } from '@/lib/utils';
import { motion, useScroll, useSpring } from 'motion/react';
import { Helmet } from 'react-helmet-async';
import { seedPost } from '@/data/seedBlogPost';
import { useInitialData } from '@/contexts/InitialDataContext';
import { getPublicBaseUrl } from '@/lib/publicBaseUrl';

export function BlogPostPage() {
  const { formatDate } = useRegion();

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const initialData = useInitialData();

  const initialPost = (initialData?.post && (initialData.post.id === id || (initialData.post as any).slug === id))
    ? initialData.post
    : null;

  const [post, setPost] = useState<BlogPost | null>(() => initialPost);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>(() => {
    if (initialPost && initialData?.relatedPosts) {
      return initialData.relatedPosts;
    }
    return [];
  });
  const [loading, setLoading] = useState(() => {
    if (initialData && 'post' in initialData) {
      return false;
    }
    return true;
  });

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (post && (post.id === id || (post as any).slug === id)) {
      return;
    }
    async function fetchPost() {
      if (!id) return;
      try {
        const res = await fetch(`/api/public/posts/${encodeURIComponent(id)}`);
        if (res.ok) {
          const data = await res.json() as BlogPost;
          if (data) {
            setPost(data);
          }
        } else if (id === 'enneagram-celebrity-examples' || id === 'seed-post-12345' || id === 'default-seed-post') {
          setPost({
            ...seedPost,
            id: id,
            createdAt: Date.now(),
            updatedAt: Date.now(),
            authorId: 'admin',
            authorName: 'Colin',
            published: true
          } as BlogPost);
        } else {
          setPost(null);
        }
      } catch (error) {
        console.warn('Error fetching post:', error);
        if (id === 'enneagram-celebrity-examples' || id === 'seed-post-12345' || id === 'default-seed-post') {
          setPost({
            ...seedPost,
            id: id,
            createdAt: Date.now(),
            updatedAt: Date.now(),
            authorId: 'admin',
            authorName: 'Colin',
            published: true
          } as BlogPost);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchPost();
  }, [id, post]);

  useEffect(() => {
    if (relatedPosts.length > 0 && post && (post.id === id || (post as any).slug === id)) {
      return;
    }
    async function fetchRelatedPosts() {
      if (!id) return;
      try {
        const res = await fetch('/api/public/posts');
        if (res.ok) {
          const fetchedPosts = await res.json() as BlogPost[];
          if (Array.isArray(fetchedPosts)) {
            const filtered = fetchedPosts.filter(p => p.id !== id && (p as any).slug !== id);
            filtered.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
            setRelatedPosts(filtered.slice(0, 3));
          }
        }
      } catch (error) {
        console.warn('Error fetching related posts:', error);
      }
    }
    fetchRelatedPosts();
  }, [id, post, relatedPosts.length]);

  if (loading) {
    return (
      <div className="min-h-screen bg-ground pt-32 pb-24 flex justify-center items-center">
        <div className="animate-pulse flex space-x-2">
          <div className="h-3 w-3 bg-rule-3 rounded-full"></div>
          <div className="h-3 w-3 bg-rule-3 rounded-full"></div>
          <div className="h-3 w-3 bg-rule-3 rounded-full"></div>
        </div>
      </div>
    );
  }

  if (!post || (!post.published)) {
    return (
      <div className="min-h-screen bg-ground pt-32 pb-24 flex flex-col items-center justify-center">
        <h1 className="text-3xl font-serif text-ink mb-4">Post not found</h1>
        <p className="text-ink/70/70 mb-8">The article you're looking for doesn't exist or has been removed.</p>
        <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
      </div>
    );
  }

  let displayContent = post?.content || '';
  // Convert any accidental <LazyImage /> tags in raw HTML to standard <img /> tags
  displayContent = displayContent.replace(/<LazyImage\b([^>]*)\/?>/gi, '<img $1 />');

  if (post && (id === 'seed-post-12345' || id === 'default-seed-post' || post.title?.toLowerCase().includes('celebrity') || (post as any).slug === 'our-best-enneagram-celebrity-examples-and-quotes')) {
    const portraitMap: Record<string, string> = {
      "Mahatma Gandhi": "https://upload.wikimedia.org/wikipedia/commons/d/d1/Portrait_Gandhi.jpg",
      "Diana, Princess of Wales": "https://upload.wikimedia.org/wikipedia/commons/5/5e/Diana%2C_Princess_of_Wales_1997_%282%29.jpg",
      "Sir Lewis Hamilton": "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d3/Prime_Minister_Keir_Starmer_meets_Sir_Lewis_Hamilton_%2854566928382%29_%28cropped%29.jpg/960px-Prime_Minister_Keir_Starmer_meets_Sir_Lewis_Hamilton_%2854566928382%29_%28cropped%29.jpg",
      "Amy Winehouse": "https://upload.wikimedia.org/wikipedia/commons/thumb/c/cf/Amy_Winehouse_f4962007_crop.jpg/960px-Amy_Winehouse_f4962007_crop.jpg",
      "Sherlock Holmes": "https://upload.wikimedia.org/wikipedia/commons/c/cd/Sherlock_Holmes_Portrait_Paget.jpg",
      "Bruce Springsteen": "https://upload.wikimedia.org/wikipedia/commons/9/93/Bruce_Springsteen_Springsteen-79_%28cropped%29.jpg",
      "Sir Richard Branson": "https://upload.wikimedia.org/wikipedia/commons/0/08/Richard_Branson_Addresses_the_Our_Ocean_Conference_2015_in_Valpara%C3%ADso_%2821783214958%29_%28cropped%29.jpg",
      "Viv Nicholson": "https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=800", // Stylistic fallback
      "Nelson Rolihlahla Mandela": "https://upload.wikimedia.org/wikipedia/commons/0/02/Nelson_Mandela_1994.jpg",
      "Ronald Reagan": "https://upload.wikimedia.org/wikipedia/commons/1/16/Official_Portrait_of_President_Reagan_1981.jpg",
      "Jeremy Irons": "https://upload.wikimedia.org/wikipedia/commons/f/f1/Jeremy_Irons.jpg",
      "Basil Fawlty": "https://upload.wikimedia.org/wikipedia/commons/thumb/f/ff/John_Cleese_%2855268018069%29.jpg/960px-John_Cleese_%2855268018069%29.jpg",
      "Lance Armstrong": "https://upload.wikimedia.org/wikipedia/commons/4/47/Lance_Armstrong_%28Tour_Down_Under_2009%29.jpg",
      "Fado": "https://upload.wikimedia.org/wikipedia/commons/thumb/0/01/Jose_malhoa_fado.jpg/960px-Jose_malhoa_fado.jpg",
      "Woody Allen": "https://upload.wikimedia.org/wikipedia/commons/8/89/Woody_Allen_Cannes_2016.jpg",
      "Robert Mugabe": "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0c/Robert_Mugabe_Official_Portrait_%284x5_cropped%29.jpg/960px-Robert_Mugabe_Official_Portrait_%284x5_cropped%29.jpg",
      "Christopher Craig": "https://upload.wikimedia.org/wikipedia/en/b/b0/Let_Him_Have_It_%28UK_film%29.jpg",
      "Annie Wilkes": "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1a/Kathy_Bates_by_Gage_Skidmore.jpg/960px-Kathy_Bates_by_Gage_Skidmore.jpg",
      "Johnny Depp": "https://upload.wikimedia.org/wikipedia/commons/2/21/Johnny_Depp_2020.jpg",
      "Rowan Atkinson": "https://upload.wikimedia.org/wikipedia/commons/a/a2/Rowan_Atkinson%2C_2011.jpg"
    };

    Object.entries(portraitMap).forEach(([name, imageUrl]) => {
      // Check if this photograph is already injected
      if (displayContent.includes(`Photograph: ${name}`)) {
        return;
      }
      const escapedName = name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
      const regex = new RegExp(`(<p>(?:(?!<p>).)*?${escapedName})`);
      const proxyUrl = `/api/image-proxy?url=${encodeURIComponent(imageUrl)}`;
      const imageHtml = `<figure class="my-8 text-center"><img src="${proxyUrl}" alt="Photograph: ${name}" class="mx-auto h-auto w-auto max-h-[500px] max-w-full rounded-xl shadow-md my-4 object-contain transition-all duration-300 hover:scale-[1.02] hover:shadow-xl cursor-pointer" loading="lazy" onerror="if (this.src.indexOf('/api/image-proxy') !== -1) { this.src = '${imageUrl}'; }" /><figcaption class="text-center text-sm text-ink/70 mt-2 italic font-medium">Photograph: ${name}</figcaption></figure>`;
      displayContent = displayContent.replace(regex, `${imageHtml}$1`);
    });
  }

  const metaDescription = cleanBlogDescription(post.excerpt || post.content);

  const baseUrl = getPublicBaseUrl();
  const postSlug = (post as any)?.slug || post?.id || id || '';
  const cleanPath = `blog/${postSlug}`;
  const canonicalUrl = `${baseUrl}/${cleanPath}`;

  const blogPostingSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    ...(post.imageUrl ? { "image": [post.imageUrl] } : {}),
    "datePublished": new Date(post.createdAt).toISOString(),
    "dateModified": new Date(post.updatedAt).toISOString(),
    "author": {
      "@type": "Person",
      "name": post.authorName || "FIVE LENS"
    },
    "publisher": {
      "@type": "Organization",
      "name": "FIVE LENS Enneagram People Development",
      "url": "https://www.fivelensenneagram.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.fivelensenneagram.com/fingerprint.webp"
      }
    },
    "description": post.excerpt || metaDescription,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    }
  };

  return (
    <>
      <Helmet>
        <title>{post.title}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogPostingSchema) }}
      />
      <motion.div
        className="fixed top-0 left-0 right-0 h-1.5 bg-rule-3 z-[60] origin-left"
        style={{ scaleX }}
      />
      <div className="min-h-screen bg-ground pt-32 pb-24">
        <article className="w-full">
          <header className="max-w-4xl mx-auto px-6 lg:px-8 mb-12">
            <div className="mb-8">
              <Breadcrumb items={[{ label: 'Insights & Articles', href: '/blog' }, { label: post.title }]} />
            </div>
            
            <div className="flex flex-wrap items-center gap-3 text-sm font-bold tracking-widest text-rule-3 uppercase mb-6">
              <span>{formatDate(post.createdAt, { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              <span className="w-1.5 h-1.5 rounded-full bg-rule-3/40"></span>
              <span>{calculateReadingTime(post)}</span>
              {post.authorName && (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-rule-3/40"></span>
                  <span className="text-ink/70/50">By {post.authorName}</span>
                </>
              )}
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-ink mb-8 leading-tight">
              {post.title}
            </h1>
          </header>

          {post.imageUrl && (
            <div className="max-w-5xl mx-auto px-6 lg:px-8 mb-16 md:mb-20">
              <div className="w-full h-[400px] md:h-[600px] overflow-hidden rounded-xl shadow-md bg-ground-deep/20">
                <LazyImage 
                  src={post.imageUrl} 
                  alt={post.title} 
                  className="w-full h-full object-cover" 
                />
              </div>
            </div>
          )}

          <div className="max-w-[65ch] mx-auto px-6 lg:px-8">
            <div 
              className="prose prose-lg md:prose-xl prose-headings:font-serif prose-headings:font-medium prose-headings:text-ink prose-headings:tracking-tight prose-p:text-ink/70 prose-p:leading-relaxed prose-a:text-rule-3 prose-a:font-medium prose-a:underline-offset-4 hover:prose-a:text-rule-3 prose-blockquote:border-l-4 prose-blockquote:border-rule-3 prose-blockquote:bg-ground-alt prose-blockquote:py-3 prose-blockquote:px-6 prose-blockquote:rounded-r-lg prose-blockquote:text-ink prose-blockquote:font-serif prose-blockquote:italic prose-strong:text-ink prose-strong:font-semibold prose-ul:list-disc prose-ul:pl-6 prose-ol:list-decimal prose-ol:pl-6 prose-li:text-ink/70 prose-li:marker:text-rule-3 prose-img:rounded-xl prose-img:shadow-md mx-auto w-full"
              dangerouslySetInnerHTML={{ __html: displayContent }}
            />
            
            <div className="mt-16 md:mt-24 bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-ground-deep/50 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="max-w-lg text-center md:text-left">
                <h3 className="text-2xl font-serif text-ink mb-3">Ready to transform your approach to people?</h3>
                <p className="text-ink/70 font-light mb-0">
                  Discover how the FIVE LENS framework can elevate your team's dynamics, build resilience, and unlock deeper engagement.
                </p>
              </div>
              <div className="shrink-0 w-full md:w-auto">
                <Button size="lg" className="w-full md:w-auto" onClick={() => navigate('/contact')}>
                  Book a Discovery Call
                </Button>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-ground-deep">
              <SocialShare title={post.title} />
            </div>
          </div>
        </article>

      {relatedPosts.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-24 pt-24 border-t border-ground-deep">
          <h2 className="text-3xl font-serif text-ink mb-12 text-center">Related Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {relatedPosts.map((relatedPost, i) => (
              <motion.article 
                key={relatedPost.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="bg-white border border-ground-deep hover:shadow-lg hover:shadow-ink/10 hover:border-ink hover:-translate-y-1 transition-all duration-300 flex flex-col h-full group"
              >
                {relatedPost.imageUrl && (
                  <div className="h-48 overflow-hidden bg-ground/50">
                    <LazyImage 
                      src={relatedPost.imageUrl} 
                      alt={relatedPost.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                    />
                  </div>
                )}
                <div className="p-8 flex flex-col flex-grow">
                  <div className="flex items-center gap-3 text-xs font-bold tracking-widest text-rule-3 uppercase mb-4">
                    <span>{formatDate(relatedPost.createdAt, { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                    <span className="w-1 h-1 rounded-full bg-rule-3/40"></span>
                    <span>{calculateReadingTime(relatedPost)}</span>
                  </div>
                  <h3 className="text-2xl font-serif text-ink mb-4 group-hover:text-rule-3 transition-colors">
                    {relatedPost.title}
                  </h3>
                  <p className="text-ink/70/70 line-clamp-3 mb-6 font-light">
                    {relatedPost.excerpt || relatedPost.content.replace(/<[^>]*>?/gm, '').substring(0, 150) + '...'}
                  </p>
                  <div className="mt-auto pt-6 border-t border-ground-deep">
                    <Link to={`/blog/${relatedPost.id}`}>
                      <span className="text-sm font-bold uppercase tracking-widest text-ink/70 group-hover:text-rule-3 transition-colors flex items-center gap-2">
                        Read Article <span className="text-xl leading-none">→</span>
                      </span>
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      )}
    </div>
    </>
  );
}
import { useRegion } from '@/contexts/RegionContext';
import { LazyImage } from '@/components/ui/LazyImage';
